<template>
  <div class="page" :style="shop?.main_image ? { backgroundImage: `url(${imgUrl(shop.main_image)})` } : {}">

    <!-- 顶部栏 -->
    <header class="topbar">
      <div class="tb-back" @click="$router.back()">
        <i class="fa-solid fa-chevron-left"></i>
      </div>
      <h1 class="tb-title">商户详情</h1>
      <FavGuide v-model:visible="showFavGuide" theme="light" />
    </header>

    <!-- 加载中 -->
    <div v-if="loading" class="loading">
      <i class="fa-solid fa-spinner fa-spin"></i>
      <span>加载中...</span>
    </div>

    <template v-else-if="shop">

      <!-- 商户图片 -->
      <div class="shop-banner">
        <div v-if="shop.main_image" class="banner-blur-bg" :style="{ backgroundImage: `url(${imgUrl(shop.main_image)})` }"></div>
        <img
          v-if="shop.main_image"
          :src="imgUrl(shop.main_image)"
          :alt="shop.name"
          @error="e => handleImgError(e, shop)"
        />
        <div v-else class="shop-banner-default">
          <i :class="shopIcon(shop.category?.name)"></i>
          <span class="shop-banner-text">{{ shop.name || '商户' }}</span>
        </div>
        <!-- 推荐角标 -->
        <div v-if="shop.sort && shop.sort >= 100" class="shop-recommend-badge">
          <img src="../assets/icon/recommend-01.png" class="recommend-badge-img" />
        </div>
      </div>

      <!-- 基本信息 -->
      <div class="info-card">
        <div class="ic-top">
          <h2 class="ic-name">{{ shop.name }}</h2>
          <!-- <div class="ic-rating">
            <i class="fa-solid fa-star"></i>
            <span>{{ shop.rating }}</span>
          </div> -->
        </div>
        <p class="ic-loc">
          <i class="fa-solid fa-location-dot"></i>
          {{ shop.address }}
        </p>
        <div class="ic-tags">
          <span v-if="shop.category" class="ic-tag blue">{{ shop.category.name }}</span>
          <span v-if="shop.town" class="ic-tag gray">{{ shop.town.name }}</span>
          <span v-if="shop.support_trade_in" class="ic-tag orange">支持以旧换新</span>
        </div>
        <div v-if="shop.coupons && shop.coupons.length" class="ic-coupon-list">
          <div v-for="coupon in shop.coupons" :key="coupon.id" class="ic-coupon">
            <div class="coupon-badge">
              <!-- <span class="badge-label">优惠</span> -->
              <template v-if="hasCouponValue(coupon)">
                <div class="badge-amount">
                  <span class="badge-symbol">¥</span>
                  <span class="badge-num">{{ coupon.reduce_amount }}</span>
                </div>
                <span class="badge-condition">满{{ coupon.threshold }}可用</span>
              </template>
              <template v-else>
                <div class="badge-no-amount">
                  <span>{{ couponTypeLabel(coupon) }}</span>
                </div>
              </template>
            </div>
            <div class="coupon-divider">
              <div class="divider-line"></div>
            </div>
            <div class="coupon-info">
              <h4 class="coupon-title">
                <span v-for="(line, idx) in formatDesc(coupon.name || '专属优惠')" :key="idx" class="coupon-title-line">{{ line }}</span>
              </h4>
              <div class="coupon-rule-row">
                <span v-if="hasCouponValue(coupon)" class="coupon-rule">满{{ coupon.threshold }}减{{ coupon.reduce_amount }}</span>
                <span v-if="coupon.remainQuota !== undefined" class="coupon-remain">
                  <i class="fa-solid fa-fire"></i> 剩余{{ coupon.remainQuota }}张
                </span>
              </div>
              <div class="coupon-meta">
                <span v-if="coupon.valid_end" class="coupon-date">
                  <i class="fa-regular fa-clock"></i> {{ coupon.valid_end }}到期
                </span>
              </div>
              <div v-if="coupon.description" class="coupon-desc">
                <i class="fa-solid fa-circle-info"></i>
                <div class="coupon-desc-content">
                  <p v-for="(line, idx) in formatDesc(coupon.description)" :key="idx">{{ line }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 数据统计 -->
      <!-- <div class="stats-row">
        <div class="sr-item">
          <span class="sr-num">{{ shop.view_count }}</span>
          <span class="sr-label">浏览量</span>
        </div>
        <div class="sr-div"></div>
        <div class="sr-item">
          <span class="sr-num">{{ shop.avg_price || '0' }}</span>
          <span class="sr-label">人均消费</span>
        </div>
        <div class="sr-div"></div>
        <div class="sr-item">
          <span class="sr-num">{{ shop.rating }}</span>
          <span class="sr-label">评分</span>
        </div>
      </div> -->

      <!-- 商户相册 -->
      <div v-if="shop.images && shop.images.length" class="section-card">
        <h3 class="sc-title"><i class="fa-solid fa-images"></i> 商户相册</h3>
        <div class="photo-grid">
          <div v-for="(img, idx) in shop.images" :key="idx" class="photo-item" @click="previewImage(imgUrl(img))">
            <img :src="imgUrl(img)" :alt="'图片' + (idx + 1)" @error="e => e.target.style.display='none'" />
          </div>
        </div>
      </div>

      <!-- 商户介绍 -->
      <div v-if="shop.description" class="section-card">
        <h3 class="sc-title"><i class="fa-solid fa-circle-info"></i> 商户介绍</h3>
        <div class="sc-desc markdown-body" v-html="descriptionHtml"></div>
      </div>

      <!-- 商户商品 -->
      <div v-if="shop.products && shop.products.length" class="section-card">
        <h3 class="sc-title"><i class="fa-solid fa-box-open"></i> 商户商品</h3>
        <div class="product-grid">
          <div v-for="product in shop.products" :key="product.id" class="product-item" @click="goProduct(product.id)">
            <div class="product-img">
              <img :src="product.image ? imgUrl(product.image) : '/no_pic.png'" :alt="product.name" />
            </div>
            <div class="product-info">
              <h4 class="product-name">{{ product.name }}</h4>
              <div class="product-price">
                <span class="price-current">¥{{ product.subsidy_price || product.price || 0 }}</span>
                <span v-if="product.original_price" class="price-original">¥{{ product.original_price }}</span>
              </div>
              <div class="product-tags">
                <span v-if="product.tag" class="product-tag" :class="product.tag">{{ product.tag }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 营业信息 -->
      <div class="section-card">
        <h3 class="sc-title"><i class="fa-solid fa-clock"></i> 营业信息</h3>
        <div class="info-row">
          <span class="ir-label"><i class="fa-solid fa-clock"></i> 营业时间：</span>
          <span class="ir-value">{{ shop.opening_hours || '暂无信息' }}</span>
        </div>
        <div class="info-row">
          <span class="ir-label"><i class="fa-solid fa-phone"></i> 联系电话：</span>
          <span class="ir-value phone" @click="callPhone">{{ shop.phone || '暂无信息' }}</span>
        </div>
        <div class="info-row">
          <span class="ir-label"><i class="fa-solid fa-location-dot"></i> 商户地址：</span>
          <span class="ir-value">{{ shop.address }}</span>
        </div>
      </div>

      <!-- 特色卖点图片：仅配置了图片的商家才显示，无配置自动隐藏 -->
      <div v-if="shopFeatureImages.length" class="section-card feature-card">
        <h3 class="sc-title"><i class="fa-solid fa-medal"></i> 特色卖点</h3>
        <div class="feature-img-grid">
          <div v-for="(img, idx) in shopFeatureImages" :key="idx" class="feature-img-item" @click="previewImage(img)">
            <img :src="img" alt="特色卖点" @error="e => e.target.style.visibility='hidden'" />
          </div>
        </div>
      </div>

      <!-- 底部操作栏 -->
      <div class="action-bar">
        <button class="ab-btn ab-call" @click="callPhone">
          <i class="fa-solid fa-phone"></i>
          <span>电话咨询</span>
        </button>
        <button class="ab-btn ab-share" @click="showShareTip = true">
          <i class="fa-solid fa-share-nodes"></i>
          <span>分享</span>
        </button>
      </div>

      <!-- 分享提示 -->
      <ShareTip v-model:visible="showShareTip" />

    </template>

    <!-- 空状态 -->
    <div v-else class="empty">
      <i class="fa-solid fa-store-slash"></i>
      <p>商户信息不存在</p>
    </div>

    <!-- 图片点击放大预览 -->
    <div v-if="previewImg" class="img-preview-mask" @click="previewImg = ''">
      <img :src="previewImg" alt="预览" />
    </div>

  </div>
</template>

<script>
import { marked } from 'marked'
import { initWechat, setShareData } from '../utils/wechat'
import ShareTip from '@/components/ShareTip.vue'
import FavGuide from '@/components/FavGuide.vue'

// 特色卖点图片配置：只针对有此需求的商家填写，无需改后台。
// key 用商家 id（即网址 /#/shop/数字 中的「数字」），value 为图片地址数组（建议 4 张，2 列展示）。
// 没有配置的商家，特色卖点区块会自动隐藏。
const FEATURE_IMAGE_MAP = {
  // 三月茶书苑：抖音团购/环境图，2×2 展示
  '32': [
    '/feature/feature_32_1.jpg',
    '/feature/feature_32_2.jpg',
    '/feature/feature_32_3.jpg',
    '/feature/feature_32_4.jpg'
  ],
  // 真心心语蛋糕店：抖音套餐图
  '39': [
    '/feature/feature_39_1.jpg',
    '/feature/feature_39_2.jpg',
    '/feature/feature_39_3.jpg',
    '/feature/feature_39_4.jpg'
  ],
  // 东道煮：抖音推荐菜
  '50': [
    '/feature/feature_50_1.jpg',
    '/feature/feature_50_2.jpg',
    '/feature/feature_50_3.jpg',
    '/feature/feature_50_4.jpg'
  ]
}

export default {
  name: 'ShopDetail',
  components: { ShareTip, FavGuide },

  data() {
    return {
      shop: null,
      loading: true,
      previewImg: '',
      showShareTip: false,
      showFavGuide: false,
      shopFaIcons: {
        '餐饮美食': 'fa-solid fa-shrimp',
        '商超便利': 'fa-solid fa-store',
        '电器数码': 'fa-solid fa-tv',
        '家居建材': 'fa-solid fa-couch',
        '汽车服务': 'fa-solid fa-car',
        '美容美发': 'fa-solid fa-scissors',
        '休闲娱乐': 'fa-solid fa-gamepad',
        '医疗健康': 'fa-solid fa-heart-pulse'
      }
    }
  },

  computed: {
    // 特色卖点图片：从前端写死的商户配置中读取，未配置的商家返回空数组（区块隐藏）
    shopFeatureImages() {
      const id = this.$route.params.id || this.shop?.id
      const list = FEATURE_IMAGE_MAP[id] || []
      if (!Array.isArray(list)) return []
      // 本地 /feature/ 目录下的图片直接返回，不拼 API 域名
      return list.map(u => {
        if (typeof u === 'string' && u.startsWith('/feature/')) return u
        return this.imgUrl(u)
      })
    },
    // 商户描述 Markdown 渲染
    descriptionHtml() {
      if (!this.shop?.description) return ''
      return marked(this.shop.description)
    }
  },

  mounted() {
    this.fetchShop()
  },

  methods: {
    async fetchShop() {
      this.loading = true
      const id = this.$route.params.id
      try {
        const r = await this.$api.get(`/api/v1/merchants/${id}`)
        this.shop = r.data.data || null
        if (this.shop) {
          this.initShare()
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },

    async initShare() {
      const wx = await initWechat()
      if (wx && this.shop) {
        setShareData(wx, {
          title: this.shop.name || '商户详情',
          desc: this.shop.description || `${this.shop.name} - 数聚吴川`,
          link: window.location.href,
          imgUrl: this.shop.mainImage || ''
        })
      }
    },

    imgUrl(path) {
      if (!path) return ''
      if (path.startsWith('http')) return path
      const base = process.env.VUE_APP_API_BASE_URL || 'http://localhost:3000'
      return base + (path.startsWith('/') ? path : '/' + path)
    },

    shopIcon(cat) {
      return this.shopFaIcons[cat] || 'fa-solid fa-store'
    },

    handleImgError(e, merchant) {
      e.target.style.display = 'none'
      const parent = e.target.parentElement
      if (parent && !parent.querySelector('.shop-banner-default')) {
        const defaultDiv = document.createElement('div')
        defaultDiv.className = 'shop-banner-default'
        defaultDiv.innerHTML = `
          <i class="${this.shopIcon(merchant.category?.name)}"></i>
          <span class="shop-banner-text">${merchant.name || '商户'}</span>
        `
        parent.appendChild(defaultDiv)
      }
    },

    callPhone() {
      if (this.shop?.phone) {
        window.location.href = `tel:${this.shop.phone}`
      }
    },

    goProduct(id) {
      this.$router.push(`/product/${id}`)
    },

    previewImage(img) {
      this.previewImg = img
    },

    formatDesc(text) {
      if (!text) return []
      // 把 "1. xxx 2. yyy" 拆成多行，普通文本保持不变
      return text.split(/(?=\d+\.[^\d])/).map(s => s.trim()).filter(Boolean)
    },

    hasCouponValue(coupon) {
      const amount = parseFloat(coupon.reduce_amount)
      const threshold = parseFloat(coupon.threshold)
      return amount > 0 || threshold > 0
    },

    couponTypeLabel(coupon) {
      const map = {
        discount: '折扣',
        gift: '礼品',
        voucher: '代金',
        cash: '现金',
        full: '满减',
        reduce: '立减'
      }
      return map[coupon.type] || '专属优惠'
    },

    openMap() {
      if (this.shop?.latitude && this.shop?.longitude) {
        window.location.href = `https://uri.amap.com/marker?position=${this.shop.longitude},${this.shop.latitude}&name=${this.shop.name}`
      }
    }
  }
}
</script>

<style scoped>
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

*, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

.page {
  width: 100%;
  min-height: 100vh;
  background: #f4f8fb;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  color: #1f2937;
  padding-top: 48px;
  padding-bottom: calc(64px + env(safe-area-inset-bottom, 0px));
  position: relative;
}

/* 全局半透明遮罩 */
.page::before {
  content: '';
  position: fixed;
  inset: 0;
  background: linear-gradient(180deg, rgba(244,248,251,0.98) 0%, rgba(244,248,251,0.95) 100%);
  z-index: 0;
  pointer-events: none;
}

/* 确保所有内容在遮罩之上 */
.page > * {
  position: relative;
  z-index: 1;
}

/* ================================================================
   顶部栏
   ================================================================ */
.topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 12px;
  background: #fff;
  border-bottom: 1px solid #f3f4f6;
}

.tb-back {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  color: #1f2937;
}

.tb-back:active { background: #f3f4f6; }
.tb-back i { font-size: 14px; }

.tb-title {
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
}

.tb-spacer { width: 32px; }

/* ================================================================
   商户图片
   ================================================================ */
.shop-banner {
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
}

/* 模糊背景层：用同一张图放大模糊填充，避免横向图上下留白突兀 */
.banner-blur-bg {
  position: absolute;
  top: -30px;
  left: -30px;
  right: -30px;
  bottom: -30px;
  background-size: cover;
  background-position: center;
  filter: blur(24px) brightness(0.7);
  transform: scale(1.15);
  z-index: 0;
}

/* 底部渐变装饰 */
.shop-banner::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: linear-gradient(180deg, transparent, rgba(244,248,251,0.8));
  pointer-events: none;
  z-index: 2;
}

.shop-banner img {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.shop-banner i {
  font-size: 60px;
  color: #93c5fd;
}

.shop-banner-default {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #9ca3af;
}

.shop-banner-default i {
  font-size: 60px;
  color: #0167CC;
  opacity: 0.5;
  margin-bottom: 12px;
}

.shop-banner-text {
  font-size: 16px;
  font-weight: 600;
  color: #6b7280;
  max-width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 推荐角标 */
.shop-recommend-badge {
  position: absolute;
  top: -5px;
  left: 8px;
  display: flex;
  align-items: center;
  z-index: 2;
  animation: shopRecommendPulse 2s ease-in-out infinite;
}

.recommend-badge-img {
  width: auto;
  height: 50px !important;
  object-fit: contain;
}

@keyframes shopRecommendPulse {
  0%, 100% {
    transform: scale(1);
    filter: drop-shadow(0 2px 4px rgba(255, 107, 53, 0.4));
  }
  50% {
    transform: scale(1.05);
    filter: drop-shadow(0 2px 8px rgba(255, 107, 53, 0.6));
  }
}

/* ================================================================
   基本信息
   ================================================================ */
.info-card {
  background: #fff;
  margin: -20px 12px 0;
  position: relative;
  z-index: 1;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,.06);
}

/* 左上角多彩装饰条 */
.info-card::before {
  content: '';
  position: absolute;
  top: 8px;
  left: -2px;
  width: 4px;
  height: 28px;
  background: linear-gradient(180deg, #0167CC, #60a5fa, #34d399);
  border-radius: 2px;
  pointer-events: none;
  z-index: 1;
}

/* 右下角多彩装饰条 */
.info-card::after {
  content: '';
  position: absolute;
  bottom: 8px;
  right: -2px;
  width: 4px;
  height: 28px;
  background: linear-gradient(180deg, #34d399, #60a5fa, #0167CC);
  border-radius: 2px;
  pointer-events: none;
  z-index: 1;
}

.ic-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.ic-name {
  font-size: 18px;
  font-weight: 800;
  flex: 1;
  margin-right: 10px;
}

.ic-rating {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #fff7ed;
  padding: 4px 8px;
  border-radius: 8px;
  flex-shrink: 0;
}

.ic-rating i { color: #f59e0b; font-size: 12px; }
.ic-rating span { font-size: 14px; font-weight: 700; color: #f59e0b; }

.ic-loc {
  font-size: 12px;
  color: #6b7280;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ic-loc i {
  flex-shrink: 0;
}

.ic-loc i { color: #9ca3af; font-size: 11px; }

.ic-tags {
  display: flex;
  gap: 6px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.ic-tag {
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 6px;
  font-weight: 500;
}

.ic-tag.blue { background: #eff6ff; color: #0167CC; }
.ic-tag.gray { background: #f3f4f6; color: #6b7280; }
.ic-tag.orange { background: #fff7ed; color: #f97316; }

.ic-coupon-list {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ic-coupon {
  position: relative;
  display: flex;
  align-items: stretch;
  background: linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%);
  border: 2px solid #ff6b6b;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(255, 107, 107, 0.25);
  transition: all .3s ease;
}

.ic-coupon::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.5) 50%,
    transparent 70%
  );
  transform: translateX(-100%);
  animation: couponShine 3s infinite;
  z-index: 1;
  pointer-events: none;
}

.ic-coupon:active {
  transform: scale(0.98);
}

@keyframes couponShine {
  0% { transform: translateX(-100%) rotate(45deg); }
  100% { transform: translateX(100%) rotate(45deg); }
}

.coupon-badge {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 12px;
  background: linear-gradient(135deg, #ff4757 0%, #ff6b6b 100%);
  color: #fff;
  width: 120px;
  flex-shrink: 0;
  overflow: hidden;
}

.coupon-badge::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: badgeShine 2s infinite;
}

@keyframes badgeShine {
  0% { left: -100%; }
  100% { left: 200%; }
}

.badge-label {
  font-size: 11px;
  font-weight: 600;
  opacity: 0.9;
  position: relative;
  z-index: 2;
  margin-bottom: 4px;
}

.badge-amount {
  display: flex;
  align-items: flex-start;
  position: relative;
  z-index: 2;
}

.badge-symbol {
  font-size: 16px;
  font-weight: 700;
  margin-top: 4px;
  margin-right: 2px;
}

.badge-num {
  font-size: 36px;
  font-weight: 900;
  line-height: 1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.badge-condition {
  font-size: 10px;
  opacity: 0.85;
  position: relative;
  z-index: 2;
  margin-top: 4px;
}

.badge-no-amount {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  margin-top: 2px;
}

.badge-no-amount span {
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  max-width: 90px;
}

.coupon-divider {
  position: relative;
  width: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.divider-line {
  width: 1px;
  height: 70%;
  background: repeating-linear-gradient(
    to bottom,
    #ff6b6b 0px,
    #ff6b6b 4px,
    transparent 4px,
    transparent 8px
  );
  opacity: 0.5;
}

.coupon-info {
  flex: 1;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.coupon-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.3;
}

.coupon-title-line {
  display: block;
  text-align: left;
}

.coupon-title-line:not(:last-child) {
  margin-bottom: 4px;
}

.coupon-rule-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.coupon-rule {
  font-size: 14px;
  font-weight: 700;
  color: #ff4757;
  background: rgba(255, 71, 87, 0.1);
  padding: 3px 8px;
  border-radius: 6px;
}

.coupon-remain {
  font-size: 11px;
  color: #ff6b6b;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 3px;
}

.coupon-remain i {
  font-size: 10px;
  animation: firePulse 1.5s ease-in-out infinite;
}

@keyframes firePulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.2); }
}

.coupon-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #666;
}

.coupon-date {
  display: flex;
  align-items: center;
  gap: 4px;
}

.coupon-date i {
  font-size: 11px;
  color: #999;
}

.coupon-desc {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  color: #e74c3c;
  background: rgba(255, 255, 255, 0.8);
  padding: 8px 10px;
  border-radius: 8px;
  line-height: 1.5;
  border: 1px dashed #ffcdd2;
}

.coupon-desc i {
  margin-top: 2px;
  font-size: 11px;
  color: #ff4757;
}

.coupon-desc-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.coupon-desc-content p {
  margin: 0;
  line-height: 1.5;
  text-align: left;
}

/* ================================================================
   数据统计
   ================================================================ */
.stats-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  margin: 12px 12px 0;
  border-radius: 14px;
  padding: 14px 0;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
}

.sr-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.sr-num {
  font-size: 20px;
  font-weight: 800;
  color: #0167CC;
}

.sr-label {
  font-size: 10px;
  color: #9ca3af;
}

.sr-div {
  width: 1px;
  height: 28px;
  background: #e5e7eb;
  flex-shrink: 0;
}

/* ================================================================
   内容卡片
   ================================================================ */
.section-card {
  background: #fff;
  margin: 12px 12px 0;
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
  position: relative;
  overflow: visible;
}

/* 左上角多彩装饰条 */
.section-card::before {
  content: '';
  position: absolute;
  top: 8px;
  left: -2px;
  width: 4px;
  height: 24px;
  background: linear-gradient(180deg, #f59e0b, #f97316, #ef4444);
  border-radius: 2px;
  pointer-events: none;
  z-index: 1;
}

/* 右下角多彩装饰条 */
.section-card::after {
  content: '';
  position: absolute;
  bottom: 8px;
  right: -2px;
  width: 4px;
  height: 24px;
  background: linear-gradient(180deg, #ef4444, #f97316, #f59e0b);
  border-radius: 2px;
  pointer-events: none;
  z-index: 1;
}

.sc-title {
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  position: relative;
}

/* 标题下方渐变装饰线 */
.sc-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 50px;
  height: 3px;
  background: linear-gradient(90deg, #0167CC, #60a5fa, #34d399, #f59e0b, #ef4444);
  border-radius: 2px;
}

.sc-title i { color: #0167CC; font-size: 14px; }

.sc-desc {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.7;
}

.markdown-body {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.7;

  :deep(h1), :deep(h2), :deep(h3), :deep(h4) {
    margin: 12px 0 8px;
    font-weight: 600;
    color: #374151;
  }

  :deep(h1) { font-size: 20px; }
  :deep(h2) { font-size: 18px; }
  :deep(h3) { font-size: 16px; }
  :deep(h4) { font-size: 14px; }

  :deep(p) {
    margin: 8px 0;
  }

  :deep(ul), :deep(ol) {
    padding-left: 20px;
    margin: 8px 0;
  }

  :deep(li) {
    margin: 4px 0;
  }

  :deep(code) {
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    color: #e11d48;
  }

  :deep(pre) {
    background: #1f2937;
    color: #e5e7eb;
    padding: 12px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 8px 0;

    code {
      background: transparent;
      color: inherit;
      padding: 0;
    }
  }

  :deep(blockquote) {
    border-left: 3px solid #0167CC;
    padding-left: 12px;
    margin: 8px 0;
    color: #6b7280;
  }

  :deep(a) {
    color: #0167CC;
    text-decoration: none;
  }

  :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 8px 0;
    display: block;
  }

  :deep(pre) {
    max-width: 100%;
    overflow-x: auto;
  }

  :deep(table) {
    max-width: 100%;
    overflow-x: auto;
    display: block;
  }

  :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 8px 0;

    th, td {
      border: 1px solid #e5e7eb;
      padding: 8px;
      text-align: left;
    }

    th {
      background: #f9fafb;
      font-weight: 600;
    }
  }
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px solid #f9fafb;
  gap: 12px;
}

.info-row:last-child { border-bottom: none; }

.ir-label {
  font-size: 12px;
  color: #6b7280;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ir-label i {
  color: #0167CC;
  font-size: 12px;
  width: 14px;
  text-align: center;
}

.ir-value {
  font-size: 13px;
  color: #1f2937;
  text-align: right;
  word-break: break-all;
  line-height: 1.5;
  flex: 1;
  min-width: 0;
}

.ir-value.phone {
  color: #0167CC;
  font-weight: 600;
  cursor: pointer;
}

/* ================================================================
   特色卖点（图片网格）
   ================================================================ */
.feature-img-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.feature-img-item {
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  overflow: hidden;
  background: #f3f4f6;
  cursor: pointer;
  transition: transform .15s ease;
}

.feature-img-item:active {
  transform: scale(0.98);
}

.feature-img-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* 商品列表 */
.product-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.product-item {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s;
}

.product-item:active {
  transform: scale(0.98);
}

.product-img {
  height: 100px;
  background: #f3f4f6;
  overflow: hidden;
}

.product-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.product-img-default {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d1d5db;
  font-size: 24px;
}

.product-info {
  padding: 10px;
}

.product-name {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-price {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 4px;
}

.price-current {
  font-size: 16px;
  font-weight: 700;
  color: #f97316;
}

.price-original {
  font-size: 11px;
  color: #9ca3af;
  text-decoration: line-through;
}

.product-tags {
  display: flex;
  gap: 4px;
}

.product-tag {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  background: #fef3c7;
  color: #d97706;
}

.product-tag.热销 {
  background: #fee2e2;
  color: #dc2626;
}

.product-tag.上新 {
  background: #dbeafe;
  color: #2563eb;
}

.product-tag.补贴 {
  background: #d1fae5;
  color: #059669;
}

/* 图片点击放大预览 */
.img-preview-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.img-preview-mask img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
  object-fit: contain;
}

/* ================================================================
   商户相册
   ================================================================ */
.photo-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.photo-item {
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }
}

.photo-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ================================================================
   底部操作栏
   ================================================================ */
.action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 10px;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid #f3f4f6;
  z-index: 50;
  border-radius: 30px 30px 0 0;
}

.ab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 0;
  border: none;
  border-radius: 22px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.ab-call {
  background: #f3f4f6;
  color: #374151;
}

.ab-call:active { background: #e5e7eb; }

.ab-share {
  background: #07c160;
  color: #fff;
}

.ab-share:active { background: #06ad56; }

.ab-nav {
  background: linear-gradient(135deg, #0167CC, #014d99);
  color: #fff;
}

.ab-nav:active { opacity: .85; }

/* ================================================================
   空状态 / 加载
   ================================================================ */
.empty, .loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  color: #9ca3af;
}

.empty i, .loading i {
  font-size: 40px;
  margin-bottom: 12px;
  opacity: .5;
}

/* ================================================================
   响应式
   ================================================================ */
@media (max-width: 360px) {
  .shop-banner { height: 160px; }
  .ic-name { font-size: 16px; }
  .sr-num { font-size: 17px; }
  .feature-img-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; }
}

@media (min-width: 481px) {
  .shop-banner { height: 260px; }
  .info-card { margin: -24px 16px 0; padding: 20px; }
  .ic-name { font-size: 20px; }
  .stats-row { margin: 16px 16px 0; }
  .section-card { margin: 16px 16px 0; padding: 18px; }
  .photo-grid { grid-template-columns: repeat(4, 1fr); }
  .feature-img-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
}
</style>
